/**
 * The unified school print dialog — the ONE dialog every school-side print
 * button opens (Production Records, cafeteria History, Serve, Today), per
 * Sheila's "make the print buttons all do the same thing". Date presets +
 * day pills + document sections; submit opens `/school/print` in a new tab.
 * "Download as CSV" beside it assembles the SAME packet in place (the
 * `useSchoolPrintJob` fan-out the print page runs, plus the per-student
 * order rows for the Orders file) and saves one CSV per
 * document kind, so the spreadsheet and the paper always agree.
 *
 * A native <form onSubmit> (app Forms rule): Print is the sole
 * `type="submit"`; Download as CSV, pills, presets, and checkboxes are all
 * `type="button"` / native inputs. The form wraps the scrollable `DialogBody`, so it carries
 * `flex min-h-0 flex-1 flex-col` per the Forms rule. Registered in
 * `CONVERTED_FORMS` (client/test/components/enter-submit-conformance.test.ts).
 */

import { Alert, AlertDescription } from '@ordo/ui/alert'
import { Button } from '@ordo/ui/button'
import { Checkbox } from '@ordo/ui/checkbox'
import { cn } from '@ordo/ui/cn'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@ordo/ui/dialog'
import { Input } from '@ordo/ui/input'
import { LoadingButton } from '@ordo/ui/loading-button'
import { toast } from '@ordo/ui/sonner'
import { AlertTriangle, Check, Download } from 'lucide-react'
import { useEffect, useId, useState } from 'react'
import { useNavigate } from 'react-router'

import { useStudentOrdersQuery } from '@/api/school-orders'
import { DayPills } from '@/components/print/day-pills'
import { SchoolPrintRosterControls } from '@/components/school-print/school-print-roster-controls'
import { useSchoolPrintJob } from '@/components/school-print/use-school-print-job'
import {
  type SchoolPrintPreset,
  type SchoolPrintSections,
  useSchoolPrintPreview,
} from '@/components/school-print/use-school-print-preview'
import { formatServiceDateLabel, formatShortServiceDateLabel } from '@/lib/cafeteria/format'
import { buildSchoolPrintCsvFiles, downloadSchoolPrintCsvFiles } from '@/lib/school-print/csv'
import type { SchoolPrintRequest } from '@/lib/school-print/request'

const PRESET_LABEL: Record<SchoolPrintPreset, string> = {
  today: 'Today',
  thisWeek: 'This week',
  nextWeek: 'Next week',
  custom: 'Custom',
}

const PILL_FACE: Record<number, string> = { 0: 'Su', 1: 'M', 2: 'Tu', 3: 'W', 4: 'Th', 5: 'F', 6: 'Sa' }

function pillFace(iso: string): string {
  return PILL_FACE[new Date(`${iso}T00:00:00Z`).getUTCDay()] ?? '?'
}

/** Shared pill-toggle look for presets + grouping (focus ring matches Checkbox). */
const TOGGLE_PILL =
  'rounded-full border font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1'
const TOGGLE_PILL_ON = 'border-primary bg-primary/10 text-primary'
const TOGGLE_PILL_OFF = 'border-border bg-background text-foreground hover:bg-muted'

export interface SchoolPrintDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  locationId: string | null
  /** Kiosk sessions can't read record documents — offer pass-out only. */
  hideRecordsSection?: boolean
  /** Per-surface starting selection (see `useSchoolPrintPreview`). */
  defaultSections?: Partial<SchoolPrintSections>
  /** Seed onto a specific day (serve preview) — see `useSchoolPrintPreview`. */
  initialDay?: string
  /**
   * A multi-campus school's campuses (PROD-1279 part two). With two or more
   * the dialog opens with a Campus row and prints ONE campus at a time,
   * `locationId` being the one it starts on; the Production Records page has
   * no campus tabs to pick it from any more.
   */
  campuses?: ReadonlyArray<{ id: string; name: string }>
}

export function SchoolPrintDialog({
  open,
  onOpenChange,
  locationId,
  hideRecordsSection,
  defaultSections,
  initialDay,
  campuses,
}: SchoolPrintDialogProps) {
  const servingSheetsId = useId()
  const navigate = useNavigate()
  const [campusId, setCampusId] = useState<string | null>(locationId)
  const campusPick = Boolean(campuses && campuses.length > 1)
  const activeLocationId = campusPick ? (campuses?.find((c) => c.id === campusId)?.id ?? locationId) : locationId
  const preview = useSchoolPrintPreview({
    locationId: activeLocationId,
    open,
    hideRecordsSection,
    defaultSections,
    initialDay,
  })

  function handleSubmit(event: React.FormEvent<HTMLFormElement>): void {
    event.preventDefault()
    if (!preview.printUrl) {
      return
    }
    const win = window.open(preview.printUrl, '_blank')
    if (!win) {
      // Popup blocked — same-tab navigation is the graceful fallback.
      void navigate(preview.printUrl)
    }
    onOpenChange(false)
  }

  // Download as CSV: freeze the selection as a request, let the shared
  // packet assembly fetch it, then save the files once it settles. A null
  // request keeps every query idle, so the dialog costs nothing until the
  // button is pressed.
  const [csvRequest, setCsvRequest] = useState<SchoolPrintRequest | null>(null)
  const csvJob = useSchoolPrintJob(csvRequest)
  // The Orders file is the per-student report, not the paper tally — its
  // rows come from their own endpoint, for the whole span; the builder
  // narrows to the selected days.
  const csvWantOrders = Boolean(csvRequest?.sections.has('orders'))
  const csvStudentOrders = useStudentOrdersQuery(
    csvRequest && csvWantOrders
      ? { schoolLocationId: csvRequest.locationId, from: csvRequest.range.from, to: csvRequest.range.to }
      : null,
  )
  const csvPending = csvRequest !== null
  useEffect(() => {
    if (!csvRequest || csvJob.pending || (csvWantOrders && csvStudentOrders.isPending)) {
      return
    }
    setCsvRequest(null)
    if (csvJob.historyError) {
      toast.error('Couldn’t load this school’s days. Try again.')
      return
    }
    const files = buildSchoolPrintCsvFiles({
      job: csvJob.job,
      documentsById: csvJob.documentsById,
      studentOrders: csvWantOrders && csvStudentOrders.data ? csvStudentOrders.data.rows : null,
      selectedDays: csvRequest.selectedDays,
    })
    if (files.length === 0) {
      if (csvWantOrders && csvStudentOrders.isError) {
        toast.error('Orders couldn’t be loaded. Try again.')
      } else {
        toast.info('Nothing to export for this selection.')
      }
      return
    }
    downloadSchoolPrintCsvFiles(files)
    if (csvJob.recordsError) {
      toast.warning('Production records couldn’t be loaded. Exported the rest without them.')
    }
    if (csvWantOrders && csvStudentOrders.isError) {
      toast.warning('Orders couldn’t be loaded. Exported the rest without them.')
    }
    onOpenChange(false)
  }, [csvRequest, csvJob, csvWantOrders, csvStudentOrders, onOpenChange])
  useEffect(() => {
    if (!open) {
      setCsvRequest(null)
    }
  }, [open])

  // "Thursday, Aug 7" for one day; "Mon 08/10 – Fri 08/14 · 5 service days"
  // across a range (with the picked subset when pills excluded some).
  const withService = preview.dayOptions.filter((option) => option.hasService).length
  const rangeLine = (() => {
    if (!preview.range || preview.historyPending) {
      return null
    }
    if (preview.selectedDays.length === 0) {
      return null
    }
    if (preview.range.days.length === 1) {
      return <span className="font-medium text-foreground">{formatServiceDateLabel(preview.range.days[0]!)}</span>
    }
    const span = `${formatShortServiceDateLabel(preview.range.from)} – ${formatShortServiceDateLabel(preview.range.to)}`
    const daysNote =
      preview.selectedDays.length === withService
        ? `${preview.selectedDays.length} service ${preview.selectedDays.length === 1 ? 'day' : 'days'}`
        : `${preview.selectedDays.length} of ${withService} service days selected`
    return (
      <>
        <span className="font-medium text-foreground">{span}</span> · {daysNote}
      </>
    )
  })()

  const recordsChip = `${preview.counts.records} ${preview.counts.records === 1 ? 'form' : 'forms'}${
    preview.counts.filled > 0 ? ` · ${preview.counts.filled} filled` : ''
  }`

  // "Serving sheets" is the app's name for this paper (the old dialog was
  // titled "Print serving sheets") — the title stays put, and the description
  // says what the selection will ACTUALLY print. Named rosters exist only
  // where parent/admin orders do, so a school-order-only selection gets
  // count wording and none of the roster-only affordances (A–Z grouping,
  // the student-names note).
  const passoutDescription =
    preview.passoutComposition === 'school'
      ? 'Meal counts by grade or homeroom, with boxes to mark servings by hand.'
      : preview.passoutComposition === 'parent'
        ? 'Each student’s order, with a box to check off as meals go out.'
        : 'Student rosters for parent-ordered meals, plus count sheets for school-ordered meals.'

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[560px]">
        <DialogHeader>
          <DialogTitle>Print</DialogTitle>
          <DialogDescription>
            {preview.recordsAvailable
              ? 'Production records and serving sheets, organized day by day.'
              : 'Serving sheets for each day of meal service.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <DialogBody className="flex flex-col gap-6">
            {campusPick ? (
              <section className="flex flex-col gap-2.5" data-testid="print-campuses">
                <p className="text-sm font-medium text-foreground">Campus</p>
                <div className="flex flex-wrap items-center gap-2" role="radiogroup" aria-label="Campus">
                  {(campuses ?? []).map((campus) => {
                    const on = campus.id === activeLocationId
                    return (
                      <button
                        key={campus.id}
                        type="button"
                        role="radio"
                        aria-checked={on}
                        onClick={() => setCampusId(campus.id)}
                        className={cn(TOGGLE_PILL, 'px-4 py-1.5 text-sm', on ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF)}>
                        {campus.name}
                      </button>
                    )
                  })}
                </div>
              </section>
            ) : null}

            <section className="flex flex-col gap-2.5">
              <p className="text-sm font-medium text-foreground">Date range</p>
              <div className="flex flex-wrap items-center gap-2">
                {(['today', 'thisWeek', 'nextWeek', 'custom'] as const).map((presetKey) => (
                  <button
                    key={presetKey}
                    type="button"
                    onClick={() => preview.selectPreset(presetKey)}
                    aria-pressed={preview.preset === presetKey}
                    className={cn(
                      TOGGLE_PILL,
                      'px-4 py-1.5 text-sm',
                      preview.preset === presetKey ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF,
                    )}>
                    {PRESET_LABEL[presetKey]}
                  </button>
                ))}
              </div>
              {preview.preset === 'custom' ? (
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    aria-label="From"
                    value={preview.customFrom}
                    onChange={(event) => preview.setCustomFrom(event.target.value)}
                    className="w-40"
                  />
                  <span className="text-sm text-muted-foreground">to</span>
                  <Input
                    type="date"
                    aria-label="To"
                    value={preview.customTo}
                    onChange={(event) => preview.setCustomTo(event.target.value)}
                    className="w-40"
                  />
                </div>
              ) : null}
              {preview.range && preview.range.days.length > 1 ? (
                <DayPills
                  days={preview.dayOptions.map((option) => ({
                    iso: option.iso,
                    pillLabel: pillFace(option.iso),
                    shortDateLabel: formatShortServiceDateLabel(option.iso),
                    hasService: option.hasService,
                    selected: preview.selectedDays.includes(option.iso),
                  }))}
                  onToggle={preview.toggleDay}
                />
              ) : null}
              {rangeLine ? <p className="text-xs leading-relaxed text-muted-foreground">{rangeLine}</p> : null}
              {preview.range && !preview.historyPending && preview.selectedDays.length === 0 ? (
                <p className="text-sm text-muted-foreground">No service scheduled in this range.</p>
              ) : null}
            </section>

            {preview.mealtimeOptions.length > 1 ? (
              // Which mealtimes print (PROD-1279 part two). Every one is in by
              // default; an unsigned one prints as a blank hand-fill form and
              // says so on its pill.
              <section className="flex flex-col gap-2.5" data-testid="print-mealtimes">
                <p className="text-sm font-medium text-foreground">Mealtimes</p>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={preview.selectAllMealtimes}
                    aria-pressed={preview.selectedMealtimes.length === preview.mealtimeOptions.length}
                    className={cn(
                      TOGGLE_PILL,
                      'px-4 py-1.5 text-sm',
                      preview.selectedMealtimes.length === preview.mealtimeOptions.length
                        ? TOGGLE_PILL_ON
                        : TOGGLE_PILL_OFF,
                    )}>
                    All mealtimes
                  </button>
                  {preview.mealtimeOptions.map((option) => {
                    const on = preview.selectedMealtimes.includes(option.mealtime)
                    const blank = option.records - option.signed
                    return (
                      <button
                        key={option.mealtime}
                        type="button"
                        onClick={() => preview.toggleMealtime(option.mealtime)}
                        aria-pressed={on}
                        data-testid={`print-mealtime-${option.mealtime}`}
                        className={cn(
                          TOGGLE_PILL,
                          'inline-flex items-center gap-1.5 px-4 py-1.5 text-sm',
                          on ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF,
                        )}>
                        {option.records > 0 && blank === 0 ? (
                          <Check className="h-3.5 w-3.5 text-success-foreground" strokeWidth={3} aria-hidden />
                        ) : null}
                        {option.label}
                        {blank > 0 ? (
                          <span className="rounded-full bg-amber-500/10 px-1.5 py-px text-[10px] font-semibold tracking-wide text-amber-700 uppercase dark:text-amber-400">
                            {blank} blank
                          </span>
                        ) : null}
                      </button>
                    )
                  })}
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  A mealtime that is not signed yet prints as a blank form to fill in by hand.
                </p>
              </section>
            ) : null}

            <section className="flex flex-col gap-2.5">
              <p className="text-sm font-medium text-foreground">Documents</p>

              {preview.recordsAvailable ? (
                <label
                  className={cn(
                    'flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors',
                    preview.sections.records
                      ? 'border-primary/40 bg-primary/[0.04]'
                      : 'border-border hover:border-muted-foreground/30 hover:bg-muted/40',
                  )}>
                  <Checkbox
                    className="mt-0.5"
                    checked={preview.sections.records}
                    onCheckedChange={(checked) =>
                      preview.setSections({ ...preview.sections, records: checked === true })
                    }
                  />
                  <span className="min-w-0">
                    <span className="flex items-baseline gap-2 text-sm font-medium">
                      Production records
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal tabular-nums text-muted-foreground">
                        {recordsChip}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      The USDA form for each mealtime. Days that haven&apos;t been signed yet print blank, to fill in by
                      hand.
                    </span>
                  </span>
                </label>
              ) : null}

              <div
                className={cn(
                  'flex items-start gap-3 rounded-lg border p-3.5 transition-colors',
                  preview.sections.passout
                    ? 'border-primary/40 bg-primary/[0.04]'
                    : 'border-border hover:border-muted-foreground/30 hover:bg-muted/40',
                )}>
                <Checkbox
                  id={servingSheetsId}
                  className="mt-0.5"
                  checked={preview.sections.passout}
                  onCheckedChange={(checked) => preview.setSections({ ...preview.sections, passout: checked === true })}
                />
                <div className="min-w-0">
                  <label htmlFor={servingSheetsId} className="block cursor-pointer">
                    <span className="flex items-baseline gap-2 text-sm font-medium">
                      Serving sheets
                      <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal tabular-nums text-muted-foreground">
                        {preview.counts.passout} {preview.counts.passout === 1 ? 'sheet' : 'sheets'}
                      </span>
                    </span>
                    <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                      {passoutDescription}
                    </span>
                  </label>
                  {preview.sections.passout && preview.passoutHasRoster ? (
                    <SchoolPrintRosterControls
                      noun={preview.rosterNoun}
                      options={preview.roster.options}
                      selected={preview.roster.labels}
                      onSelectionChange={preview.roster.selectLabels}
                      grouped={preview.roster.grouped}
                      onGroupedChange={preview.roster.setGrouped}
                      groupingDisabled={preview.roster.groupingDisabled}
                      pending={preview.roster.pending}
                      error={preview.roster.error}
                    />
                  ) : null}
                </div>
              </div>

              <label
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-lg border p-3.5 transition-colors',
                  preview.sections.orders
                    ? 'border-primary/40 bg-primary/[0.04]'
                    : 'border-border hover:border-muted-foreground/30 hover:bg-muted/40',
                )}>
                <Checkbox
                  className="mt-0.5"
                  checked={preview.sections.orders}
                  onCheckedChange={(checked) => preview.setSections({ ...preview.sections, orders: checked === true })}
                />
                <span className="min-w-0">
                  <span className="flex items-baseline gap-2 text-sm font-medium">
                    Orders
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-normal tabular-nums text-muted-foreground">
                      {preview.splitOrdersByBucket && preview.orderSheetCount !== preview.counts.orders
                        ? `up to ${preview.orderSheetCount}`
                        : preview.orderSheetCount}{' '}
                      {preview.orderSheetCount === 1 ? 'sheet' : 'sheets'}
                    </span>
                  </span>
                  <span className="mt-1 block text-xs leading-relaxed text-muted-foreground">
                    One sheet per day: every ordered item with school and parent orders combined, by grade group.
                  </span>
                  {preview.sections.orders && preview.orderColumnOptions.length > 1 ? (
                    // Bucket filter (grade band / homeroom): default is every
                    // group; picking chips scopes the printed sheets to just
                    // those groups.
                    <span className="mt-2.5 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => preview.clearOrderColumns()}
                        aria-pressed={preview.selectedOrderColumnIds.length === 0}
                        className={cn(
                          TOGGLE_PILL,
                          'px-3 py-1 text-xs',
                          preview.selectedOrderColumnIds.length === 0 ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF,
                        )}>
                        All groups
                      </button>
                      {preview.orderColumnOptions.map((option) => (
                        <button
                          key={option.id}
                          type="button"
                          onClick={() => preview.toggleOrderColumn(option.id)}
                          aria-pressed={preview.selectedOrderColumnIds.includes(option.id)}
                          className={cn(
                            TOGGLE_PILL,
                            'px-3 py-1 text-xs',
                            preview.selectedOrderColumnIds.includes(option.id) ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF,
                          )}>
                          {option.label}
                        </button>
                      ))}
                    </span>
                  ) : null}
                  {preview.sections.orders &&
                  preview.selectedOrderColumnIds.length > 0 &&
                  preview.selectedOrderColumnIds.length < preview.orderColumnOptions.length ? (
                    <span className="mt-1.5 block text-xs leading-relaxed text-muted-foreground">
                      Only the selected groups will print. Staff and other ungrouped orders are left out.
                    </span>
                  ) : null}
                  {preview.sections.orders && preview.orderColumnOptions.length > 1 ? (
                    // PROD-1335 — one physical sheet per bucket, so each
                    // classroom can be handed its own page. A pill, not a
                    // nested checkbox+label: this lives INSIDE the section
                    // label, and a <label> cannot nest one. It matches the
                    // grouping/filter pills either side of it, and a button is
                    // interactive content, so clicking it never toggles the
                    // section checkbox.
                    <span className="mt-2.5 block">
                      <button
                        type="button"
                        onClick={() => preview.setSplitOrdersByBucket(!preview.splitOrdersByBucket)}
                        aria-pressed={preview.splitOrdersByBucket}
                        className={cn(
                          TOGGLE_PILL,
                          'px-3 py-1 text-xs',
                          preview.splitOrdersByBucket ? TOGGLE_PILL_ON : TOGGLE_PILL_OFF,
                        )}>
                        One sheet per {preview.orderBucketNoun}
                      </button>
                      {preview.splitOrdersByBucket ? (
                        <span className="mt-1.5 block text-xs leading-relaxed text-muted-foreground">
                          Each {preview.orderBucketNoun} prints on its own page. Staff and other ungrouped orders are
                          left out.
                        </span>
                      ) : null}
                    </span>
                  ) : null}
                </span>
              </label>

              {preview.passoutHasRoster ? (
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                  {preview.recordsAvailable
                    ? 'Serving sheets include student names; production records don’t.'
                    : 'Serving sheets include student names.'}
                </p>
              ) : null}
            </section>

            {preview.unconfirmedDays.length > 0 ? (
              <Alert variant="warning">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <AlertDescription>
                    Ordering is still open for{' '}
                    {preview.range && preview.range.days.length === 1
                      ? 'this day'
                      : preview.unconfirmedDays.length === 1
                        ? 'one of these days'
                        : `${preview.unconfirmedDays.length} of these days`}{' '}
                    — counts can still change. Print again after the deadline for final numbers.
                  </AlertDescription>
                </div>
              </Alert>
            ) : null}
            {preview.notMaterializedCount > 0 && preview.sections.records && preview.recordsAvailable ? (
              <p className="text-xs leading-relaxed text-muted-foreground">
                Some days don&apos;t have production records yet — they&apos;re created closer to the service day.
              </p>
            ) : null}
          </DialogBody>

          <DialogFooter className="items-center border-t border-border pt-4 sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {preview.historyPending ? (
                'Loading days…'
              ) : preview.documentCount === 0 ? (
                'Nothing to print for this selection'
              ) : (
                <>
                  <span className="font-medium tabular-nums text-foreground">{preview.documentCount}</span>{' '}
                  {preview.documentCount === 1 ? 'document' : 'documents'}
                </>
              )}
            </p>
            <div className="flex items-center gap-2">
              <LoadingButton
                type="button"
                variant="secondary"
                icon={Download}
                loading={csvPending}
                disabled={!preview.printRequest || preview.historyPending}
                onClick={() => setCsvRequest(preview.printRequest)}>
                {csvPending ? 'Preparing…' : 'Download as CSV'}
              </LoadingButton>
              <Button type="submit" disabled={!preview.printUrl || preview.historyPending || csvPending}>
                Print
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
